import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { NexsoLogo } from "@/components/app/NexsoLogo";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };

export default function Login() {
  const { login, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      const redirect = location.state?.from || "/dashboard";
      navigate(redirect);
    }
  };

  return (
    <div className="min-h-screen text-slate-900 grid lg:grid-cols-2 relative overflow-hidden nexso-app-bg" style={{ ...bodyFont }}>
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r bg-gradient-to-br from-sky-500 via-sky-600 to-sky-700 text-white">
        <Link to="/" className="flex items-center gap-2.5 relative z-10" data-testid="login-brand-link">
          <div className="h-9 w-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <span className="text-white text-lg font-bold leading-none tracking-tight" style={headingFont}>N</span>
          </div>
          <span className="font-semibold text-xl tracking-tight" style={headingFont}>Nexso</span>
        </Link>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-5">Welcome back</div>
          <h2 className="text-5xl xl:text-6xl leading-[1.05] tracking-tight font-extrabold" style={headingFont}>
            Your screens<br/>are <span className="text-white/80">waiting.</span>
          </h2>
          <p className="mt-6 text-white/85 max-w-md text-base leading-relaxed">
            Pick up where you left off. Update prices, push promotions, and refresh your TV displays in seconds.
          </p>
          <div className="mt-10 flex items-center gap-3 text-sm text-white/80">
            <Sparkles className="h-4 w-4" /> 350,000+ templates · 17 apps · zero on-site visits
          </div>
        </div>
        <div className="text-xs text-white/50 relative z-10">© {new Date().getFullYear()} Nexso</div>
        <div aria-hidden="true" className="absolute -top-32 -right-32 w-[40rem] h-[40rem] rounded-full opacity-20 bg-white blur-3xl" />
      </div>

      {/* Right form panel */}
      <div className="relative flex items-center justify-center p-6 md:p-12 bg-white">
        <form onSubmit={handleSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <NexsoLogo size="md" />
              <span className="font-semibold text-xl text-slate-900" style={headingFont}>Nexso</span>
            </Link>
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tight font-bold mb-3 text-slate-900" style={headingFont}>Sign in</h1>
          <p className="text-sm text-slate-600 mb-8">Manage your digital signage from anywhere.</p>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-sky-500"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-sky-500"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl" data-testid="login-error">{error}</div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full h-12 bg-sky-500 hover:bg-sky-600 text-white shadow-md transition-shadow"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>

          <div className="mt-6 text-sm text-slate-600">
            New here?{" "}
            <Link to="/register" className="font-semibold text-sky-600 hover:text-sky-700" data-testid="login-to-register">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
